/**
 * React bindings for @rudi/api
 *
 * Provides ApiClientProvider context so shared query hooks can access
 * the platform-specific Axios instance without importing a singleton.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { AxiosInstance } from 'axios';

const ApiClientContext = createContext<AxiosInstance | null>(null);

export interface ApiClientProviderProps {
  children: ReactNode;
  client: AxiosInstance;
}

/**
 * Provides an Axios instance to all descendant components.
 * Shared query hooks use `useApiClient()` to access it.
 */
export function ApiClientProvider({ children, client }: ApiClientProviderProps) {
  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

/**
 * Returns the Axios instance from the nearest ApiClientProvider.
 * Throws if used outside a provider.
 */
export function useApiClient(): AxiosInstance {
  const client = useContext(ApiClientContext);
  if (!client) {
    throw new Error('useApiClient must be used within an ApiClientProvider');
  }
  return client;
}
